/**
 * M7 S40: the restart reboot (plan S40; spec §16.3 step 5; integration notes S15/S20).
 *
 * - **The defence** is M3's relay reboot defence with its region table (`relink/rules.ts rebootSchedule` through
 *   S15's `rebootParams`): the reactor spins up for the region's reboot time while the region's Wrecker waves come
 *   in, and **guardians** join when a tetherstone-vein system is within `reboot.guardians.hops` (2) relay hops.
 * - **Before it starts** (`restartRefusal`): a surveyed record with a complete kit, the hazards and squatters
 *   cleared, the ship at the repair port and slow (the M3 port rules), no restart already running, and no scram
 *   retry pending.
 * - **Outcomes** (`settleRestart`, the M3 `REBOOT_END` codes through `restartOutcomeOf`):
 *   - **online:** the record goes `lit` (S15 `markLit`), the worker gets `restoreDerelict` (owner the player, the
 *     class template), **before** the registrant choice (S43) sends `registerReactivation`; an autosave.
 *   - **scram** (the objective destroyed): the fuel cells are lost, the components stay (S15 `scramKit`); the retry
 *     waits `rebootRetryOnlineDays` (1) **online** faction days (a `reactivation` countdown, so offline days never
 *     count).
 *   - **aborted** (the pilot died or left): nothing changes; the kit is kept and the restart can start again.
 *
 * Career in, result out; pure. Rare events, never per frame.
 */
import type { Career } from '../../career/types'
import type { Galaxy, StarSystem } from '../../galaxy/types'
import type { RelinkConfig } from '../../relink/config'
import { REBOOT_END, type RebootEnd } from '../../relink/reboot'
import { countdownOf, scheduleCountdown } from '../../story/countdowns'
import { rebootParams, type RebootParams } from '../../story/reactivation/eligibility'
import { kitComplete, markLit, recordRequirement, scramKit } from '../../story/reactivation/kit'
import type { SurveyCard } from '../../story/reactivation/survey'
import type { ReactivationConfig } from '../../story/schemas'
import { storyOf } from '../../story/state'
import type { StoryCommand, StoryEffect, StoryResult } from '../../story/types'
import { fillTokens } from '../world'
import { clearRefusal, portCheck, recordOf, type PortPosition, type RestoreRefusal, type SiteFacts } from './restore'
import { reactivationText, type ReactivationText } from './survey'

export type RestartRefusal = RestoreRefusal | 'incomplete' | 'retry' | 'busy'

export type RestartOutcome = 'online' | 'scram' | 'aborted'

/** The scram retry's countdown id for `poiId` (kind `reactivation`; it fires and clears on its online day). */
export function retryCountdownId(poiId: string): string {
  return `reactivation:retry:${poiId}`
}

/** Online days until a scrammed restart may retry (0 = now). */
export function retryDaysLeft(career: Career, poiId: string): number {
  const cd = countdownOf(career, retryCountdownId(poiId))
  return cd === undefined ? 0 : Math.max(0, cd.dueOnlineDay - storyOf(career).onlineDays)
}

/** The restart's defence: the region's reboot time and Wrecker waves, guardians within the hop rule of a vein. */
export function restartPlan(galaxy: Galaxy, system: Pick<StarSystem, 'id' | 'region'>, relink: RelinkConfig): RebootParams {
  return rebootParams(galaxy, system, relink)
}

/** Why the restart can't start now, or null. `running`: a restart (or relay reboot) is already under way. */
export function restartRefusal(
  career: Career,
  site: SiteFacts,
  at: PortPosition,
  cfg: ReactivationConfig,
  relink: Pick<RelinkConfig, 'portRangeM' | 'portMaxSpeed'>,
  running: boolean,
): RestartRefusal | null {
  if (site.kind === 'harrow') return 'harrow'
  if (running) return 'busy'
  const record = recordOf(career, site.poiId)
  if (record === undefined || record.frClass === undefined) return 'noRecord'
  if (record.state !== 'kit' && record.state !== 'surveyed' && record.state !== 'reverted') return 'closed'
  const blocked = clearRefusal(site, record)
  if (blocked !== null) return blocked
  const req = recordRequirement(record, cfg)
  if (req === null || record.state !== 'kit' || !kitComplete(req, record.kit)) return 'incomplete'
  if (retryDaysLeft(career, site.poiId) > 0) return 'retry'
  return portCheck(at, relink)
}

/** The M3 reboot end code as a restart outcome (a destroyed objective is a scram). */
export function restartOutcomeOf(end: RebootEnd): RestartOutcome {
  if (end === REBOOT_END.online) return 'online'
  return end === REBOOT_END.destroyed ? 'scram' : 'aborted'
}

export interface RestartSettled {
  result: StoryResult<Career>
  /** The player-facing line. */
  message: string
}

/**
 * Settles a restart's outcome for the surveyed station (see the file header). The success line names the card's
 * `FR-###` designation, never the POI id.
 */
export function settleRestart(
  career: Career,
  station: Pick<SurveyCard, 'poiId' | 'systemId' | 'designation'>,
  outcome: RestartOutcome,
  cfg: ReactivationConfig,
  text: ReactivationText = reactivationText(),
): RestartSettled {
  const { poiId, systemId, designation } = station
  const unchanged = (message: string): RestartSettled => ({ result: { career, commands: [], effects: [] }, message })
  const record = recordOf(career, poiId)
  if (record === undefined || record.frClass === undefined || outcome === 'aborted') return unchanged(text.messages.aborted)
  if (outcome === 'online') {
    const lit = markLit(career, poiId, storyOf(career).onlineDays, cfg)
    if (lit === null) return unchanged(text.refusals.incomplete)
    const commands: StoryCommand[] = [{ type: 'restoreDerelict', poiId, systemId, template: cfg.classes[record.frClass].template, owner: 'player' }]
    const effects: StoryEffect[] = [{ type: 'autosave', reason: 'reactivationLit' }]
    return { result: { career: lit, commands, effects }, message: fillTokens(text.messages.online, { designation }) }
  }
  const days = cfg.rebootRetryOnlineDays
  const scrammed = scheduleCountdown(scramKit(career, poiId), { id: retryCountdownId(poiId), kind: 'reactivation', inOnlineDays: days })
  return {
    result: { career: scrammed, commands: [], effects: [{ type: 'autosave', reason: 'reactivationScram' }] },
    message: fillTokens(text.messages.scram, { days: String(days) }),
  }
}
